import { ResponsiveModal, useConfirmationModal } from '@bridebook/aura';
import { interactedWithGuestCommsAction } from '@bridebook/web/lib/inbox/guests/analytics/actions';
import {
  CreateMessageFlowProvider,
  CreateMessageFlowStep,
  useCreateMessageFlow,
} from '@bridebook/web/lib/inbox/guests/create-message-flow/create-message-flow-context';
import { useDirtyMessageFlow } from '@bridebook/web/lib/inbox/guests/create-message-flow/dirty-message-flow-context';
import { RecipientsProvider } from '@bridebook/web/lib/inbox/guests/create-message-flow/recipients/recipients-context';
import { useDispatch } from '@bridebook/web/lib/utils';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { MessageForm } from './step-1-message-form/message-form';
import { Recipients } from './step-2-recipients/recipients';
import { Summary } from './step-3-summary/summary';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateMessageContainer = ({ isOpen, onClose }: IProps) => {
  const { openModal } = useConfirmationModal();
  const { t } = useTranslation('inbox');
  const { isDirty } = useDirtyMessageFlow();

  const closeWithConfirmation = (onPrimaryButtonClick: () => void) => {
    // do not display modal if no changes were made
    if (!isDirty) {
      onClose();
      return;
    }

    openModal({
      title: t('guests.createMessageFlow.interceptModal.title'),
      text: t('guests.createMessageFlow.interceptModal.text'),
      primaryButtonText: t('guests.createMessageFlow.interceptModal.primaryButtonText'),
      onPrimaryButtonClick: () => {
        onClose();
        onPrimaryButtonClick();
      },
      secondaryButtonText: t('guests.createMessageFlow.interceptModal.secondaryButtonText'),
    });
  };

  if (!isOpen) return null;

  return (
    <CreateMessageFlowProvider forceCloseModal={onClose}>
      <Modal isOpen={isOpen} onClose={closeWithConfirmation} />
    </CreateMessageFlowProvider>
  );
};

const Steps = () => {
  const { currentStep } = useCreateMessageFlow();

  switch (currentStep) {
    case CreateMessageFlowStep.Message:
      return <MessageForm />;
    case CreateMessageFlowStep.Recipients:
      return (
        <RecipientsProvider>
          <Recipients />
        </RecipientsProvider>
      );
    case CreateMessageFlowStep.Summary:
      return <Summary />;
    default:
      return null;
  }
};

interface IModalProps {
  isOpen: boolean;
  onClose: (onConfirm: () => void) => void;
}

const Modal = ({ isOpen, onClose }: IModalProps) => {
  const { currentStep } = useCreateMessageFlow();
  const dispatch = useDispatch();

  const sectionValue = useMemo(() => {
    switch (currentStep) {
      case CreateMessageFlowStep.Message:
        return 'writeMessage';
      case CreateMessageFlowStep.Recipients:
        return 'selectRecipients';
      case CreateMessageFlowStep.Summary:
        return 'messageSummary';
    }
  }, [currentStep]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose(() => {
        dispatch(
          interactedWithGuestCommsAction({
            action: 'clicked',
            location: 'guestComms',
            section: sectionValue,
            element: 'leaveButton',
            elementLabel: 'Leave anyway',
          }),
        );
      });
    }
  };

  return (
    <ResponsiveModal isOpen={isOpen} onOpenChange={handleOpenChange} className="z-[10000]">
      <Steps />
    </ResponsiveModal>
  );
};
